import { 
  getAuth, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut, 
  signInWithRedirect,
  signInWithPopup,
  GoogleAuthProvider,
  getRedirectResult,
  onAuthStateChanged
} from 'firebase/auth';
import { 
  User, 
  RegisterParams, 
  AuthError,
  UserLocation
} from '../types/auth.types';
import { api } from '../../services/api';
import { API_CONFIG } from '../../utils/apiUrlConfig';

/**
 * Service d'authentification
 * Fournit toutes les méthodes pour gérer l'authentification de l'utilisateur
 */
export class AuthService {
  private static instance: AuthService;
  private auth = getAuth();

  // Pattern Singleton pour s'assurer qu'une seule instance existe
  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService();
    }
    return AuthService.instance;
  }

  /**
   * Connexion avec email et mot de passe
   */
  async loginWithEmailPassword(email: string, password: string): Promise<User> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const token = await userCredential.user.getIdToken();
      
      // Configure l'en-tête d'autorisation pour les requêtes API
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Stocke le token dans le sessionStorage
      this.storeToken(token, userCredential.user.uid);
      
      // Récupère les informations complètes de l'utilisateur depuis l'API
      return this.getCurrentUser();
    } catch (error: any) {
      console.error('Erreur de connexion:', error);
      throw new AuthError(
        this.getFirebaseErrorMessage(error.code) || error.message, 
        error.code
      );
    }
  }

  /**
   * Connexion avec Google
   */
  async loginWithGoogle(): Promise<void> {
    try {
      console.log('Démarrage connexion Google', new Date().toISOString());
      
      // Stocke des informations de débogage
      sessionStorage.setItem('auth_redirect_triggered', 'true');
      sessionStorage.setItem('auth_redirect_timestamp', new Date().toISOString());
      
      const provider = new GoogleAuthProvider();
      provider.addScope('profile');
      provider.addScope('email');
      
      // Configuration explicite pour utiliser la redirection
      provider.setCustomParameters({
        'prompt': 'select_account'
      });
      
      // Nettoyer les éventuelles anciennes tentatives
      sessionStorage.removeItem('firebase:pendingRedirect');

      // Déterminer si on doit utiliser popup ou redirect
      // En environnement de développement, utiliser le popup pour éviter les problèmes de redirection
      const isDevelopment = import.meta.env.VITE_ENV === 'development';
      
      if (isDevelopment) {
        console.log('Utilisation du popup pour l\'environnement de développement...');
        const result = await signInWithPopup(this.auth, provider);
        
        // Traiter le résultat directement (car popup est synchrone)
        if (result) {
          const token = await result.user.getIdToken();
          this.storeToken(token, result.user.uid);
          
          // Configure l'en-tête d'autorisation pour les requêtes API
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // Vérifier si c'est un nouvel utilisateur qui n'existe pas dans notre base de données
          try {
            // Tenter de récupérer l'utilisateur existant
            await this.getCurrentUser();
          } catch (getUserError: any) {
            // Si erreur 401, c'est probablement un nouvel utilisateur
            if (getUserError.code === 'api_error' || getUserError.status === 401) {
              console.log('Utilisateur Google non trouvé dans la base de données, création d\'un nouveau profil...');
              
              // Récupérer le type d'utilisateur depuis sessionStorage
              const userType = sessionStorage.getItem('googleSignUpUserType') || 'user';
              console.log(`🔑 Création d'un compte ${userType} pour l'utilisateur Google`);
              
              // Préparer les données utilisateur de base selon RegisterUserDto
              const firstName = result.user.displayName ? result.user.displayName.split(' ')[0] : '';
              const lastName = result.user.displayName ? result.user.displayName.split(' ').slice(1).join(' ') : '';
              const name = result.user.displayName || (firstName + ' ' + lastName).trim();
              
              if (userType === 'organization') {
                // Créer l'utilisateur avec userType="organization"
                const userProfile = {
                  firebaseUid: result.user.uid,
                  email: result.user.email || '',
                  name: name,
                  firstName: firstName,
                  lastName: lastName,
                  userType: 'organization' // Important: définir le type comme organization
                };
                
                console.log('Création d\'un utilisateur de type organization:', userProfile);
                const userResponse = await api.post('/api/users/register', userProfile);
                
                if (userResponse.data) {
                  console.log('Utilisateur de type organization créé avec succès');
                }
              } else {
                // Pour un utilisateur standard (student)
                const profile = {
                  firebaseUid: result.user.uid,
                  email: result.user.email || '',
                  name: name,
                  firstName: firstName,
                  lastName: lastName,
                  userType: 'regular'  // Utiliser 'regular' au lieu de 'student' pour correspondre à l'enum accepté
                };
                
                console.log('Création d\'un utilisateur standard:', profile);
                const response = await api.post('/api/users/register', profile);
                
                if (response.data) {
                  console.log('Utilisateur standard créé avec succès dans la base de données');
                }
              }
              
              // Nettoyer le type d'utilisateur après utilisation réussie
              sessionStorage.removeItem('googleSignUpUserType');
              
              // Récupérer les données utilisateur après création
              // On ne retourne pas de valeur pour respecter la signature de la méthode
              await this.getCurrentUser();
            }
          }          
        }
      } else {
        // En production, utiliser la redirection qui est plus fiable pour le flux SSO
        console.log('Redirection vers Google... timestamp:', new Date().toISOString());
        await signInWithRedirect(this.auth, provider);
      }
    } catch (error: any) {
      console.error('Erreur de connexion Google:', error);
      throw new AuthError(
        this.getFirebaseErrorMessage(error.code) || error.message, 
        error.code
      );
    }
  }

  /**
   * Récupère le résultat de la redirection
   */
  async getRedirectResultHandler(): Promise<User | null> {
    try {
      console.log('Vérification du résultat de redirection...', new Date().toISOString());
      const result = await getRedirectResult(this.auth);
      
      if (!result) {
        console.log('Aucun résultat de redirection trouvé');
        return null;
      }
      
      console.log('Résultat de redirection trouvé!', new Date().toISOString());
      
      const token = await result.user.getIdToken();
      this.storeToken(token, result.user.uid);
      
      // Configure l'en-tête d'autorisation pour les requêtes API
      this.configureAuthHeaders();
      
      // Nettoyage des données de redirection
      sessionStorage.removeItem('auth_redirect_triggered');
      sessionStorage.removeItem('auth_redirect_timestamp');
      
      try {
        // D'abord, essayer de récupérer l'utilisateur (cas d'une connexion)
        return await this.getCurrentUser();
      } catch (getUserError: any) {
        // Si erreur 401, c'est probablement un nouvel utilisateur
        if (getUserError.code === 'api_error' || getUserError.status === 401) {
          console.log('Utilisateur non trouvé dans la base de données, création d\'un nouveau profil...');
          
          // Récupérer le type d'utilisateur depuis sessionStorage
          const userType = sessionStorage.getItem('googleSignUpUserType') || 'user';
          console.log(`🔑 Création d'un compte ${userType} pour l'utilisateur Google (redirection)`);
          
          // Préparer les données utilisateur de base
          const firstName = result.user.displayName ? result.user.displayName.split(' ')[0] : '';
          const lastName = result.user.displayName ? result.user.displayName.split(' ').slice(1).join(' ') : '';
          const name = result.user.displayName || (firstName + ' ' + lastName).trim();
          let userData;
          
          if (userType === 'organization') {
            console.log('Création d\'une organisation via redirection...');
            
            // Créer l'utilisateur avec userType="organization"
            const userProfile = {
              firebaseUid: result.user.uid,
              email: result.user.email || '',
              name: name,
              firstName: firstName,
              lastName: lastName,
              userType: 'organization' // Important: définir le type comme organization
            };
            
            console.log('Création d\'un utilisateur de type organization (redirection):', userProfile);
            const userResponse = await api.post('/api/users/register', userProfile);
            userData = userResponse.data;
            
            if (userResponse.data) {
              console.log('Utilisateur de type organization créé avec succès (redirection)');
            }
          } else {
            // Pour un utilisateur standard (student)
            const profile = {
              firebaseUid: result.user.uid,
              email: result.user.email || '',
              name: name,
              firstName: firstName,
              lastName: lastName,
              userType: 'regular'  // Utiliser 'regular' au lieu de 'student' pour correspondre à l'enum accepté
            };
            
            console.log('Création d\'un utilisateur standard (redirection):', profile);
            const response = await api.post('/api/users/register', profile);
            userData = response.data;
            
            if (response.data) {
              console.log('Utilisateur standard créé avec succès (redirection)');
            }
          }
          
          // Nettoyer le type d'utilisateur après utilisation réussie
          sessionStorage.removeItem('googleSignUpUserType');
          console.log(`${userType === 'organization' ? 'Organisation' : 'Utilisateur'} créé(e) avec succès:`, userData);
          
          // Rediriger l'utilisateur vers la page events après la création réussie
          window.location.href = '/events';
          
          return userData;
        }
        
        // Si c'est une autre erreur, la propager
        throw getUserError;
      }
    } catch (error: any) {
      console.error('Erreur de récupération du résultat de redirection:', error);
      throw new AuthError(
        this.getFirebaseErrorMessage(error.code) || error.message, 
        error.code
      );
    }
  }

  /**
   * Inscription avec email et mot de passe
   */
  async registerWithEmailPassword(params: RegisterParams): Promise<User> {
    try {
      const { email, password, ...userData } = params;
      
      // Crée l'utilisateur dans Firebase
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      const token = await userCredential.user.getIdToken();
      
      // Configure l'en-tête d'autorisation pour les requêtes API
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Stocke le token dans le sessionStorage
      this.storeToken(token, userCredential.user.uid);
      
      // S'assurer que le chemin API est correct
      const apiPath = '/api/users/register';
      console.log(`📍 Enregistrement utilisateur - Appel API vers: ${apiPath}`);
      
      // Crée le profil utilisateur dans notre API backend
      const response = await api.post(apiPath, {
        ...userData,
        firebaseUid: userCredential.user.uid,
        email: email,
        // S'assurer que le userType est spécifié (requis par l'enum dans le DTO)
        userType: userData.userType || 'regular'
      });
      
      return response.data;
    } catch (error: any) {
      console.error('Erreur d\'inscription:', error);
      throw new AuthError(
        this.getFirebaseErrorMessage(error.code) || error.message, 
        error.code
      );
    }
  }

  /**
   * Déconnexion
   */
  async logout(): Promise<void> {
    try {
      // Supprime tous les tokens et données utilisateur
      this.clearAuthData();
      
      // Déconnexion de Firebase
      await signOut(this.auth);
      
      // Supprime l'en-tête d'autorisation
      delete api.defaults.headers.common['Authorization'];
    } catch (error: any) {
      console.error('Erreur de déconnexion:', error);
      throw new AuthError(
        this.getFirebaseErrorMessage(error.code) || error.message, 
        error.code
      );
    }
  }

  /**
   * Récupère l'utilisateur courant depuis l'API
   */
  async getCurrentUser(): Promise<User> {
    try {
      // Récupérer l'UID de l'utilisateur actuel pour le log
      const currentUserUID = sessionStorage.getItem('currentUserUID');
      console.log(`🔍 Récupération des informations utilisateur pour l'UID Firebase: ${currentUserUID}`);
      
      // Vérifier si nous sommes en production et journaliser la configuration API
      const isProduction = API_CONFIG.isProduction;
      console.log(`📍 Configuration API - Production: ${isProduction}, BaseURL: ${API_CONFIG.baseUrl}, ApiPath: ${API_CONFIG.apiPath}`);
      
      // Construire le chemin complet de l'API
      // S'assurer que le chemin est bien préfixé par /api
      const apiPath = '/api/users/me';
      console.log(`💰 Appel API vers: ${apiPath}`);
      
      // La requête utilise le token JWT qui contient l'UID
      // Le backend extraira l'UID du token et recherchera l'utilisateur correspondant
      const response = await api.get(apiPath);
      
      console.log(`✅ Informations utilisateur récupérées depuis la base de données pour l'UID: ${currentUserUID}`);
      return response.data;
    } catch (error: any) {
      console.error('Erreur de récupération de l\'utilisateur courant:', error);
      throw new AuthError(
        'Erreur de récupération des données utilisateur',
        'api_error'
      );
    }
  }

  /**
   * Met à jour les informations de l'utilisateur
   */
  async updateUser(userData: Partial<User>): Promise<User> {
    try {
      const response = await api.put('/api/users/me', userData);
      return response.data;
    } catch (error: any) {
      console.error('Erreur de mise à jour de l\'utilisateur:', error);
      throw new AuthError(
        'Erreur de mise à jour du profil utilisateur',
        'api_error'
      );
    }
  }

  /**
   * Met à jour la position de l'utilisateur
   */
  async updateUserLocation(location: UserLocation): Promise<User> {
    try {
      const response = await api.patch('/api/users/me/location', location);
      return response.data;
    } catch (error: any) {
      console.error('Erreur de mise à jour de la position:', error);
      throw new AuthError(
        'Erreur de mise à jour de la position',
        'api_error'
      );
    }
  }

  /**
   * Écoute les changements d'état d'authentification
   */
  onAuthStateChanged(callback: (user: any) => void): () => void {
    return onAuthStateChanged(this.auth, callback);
  }

  /**
   * Configure l'en-tête d'autorisation avec le token JWT
   */
  configureAuthHeaders(): void {
    try {
      // Récupérer l'UID de l'utilisateur actuel
      const currentUserUID = sessionStorage.getItem('currentUserUID');
      if (!currentUserUID) {
        console.log('Aucun UID utilisateur trouvé, impossible de configurer les en-têtes');
        return;
      }
      
      // Récupérer le token stocké
      const tokenKey = `firebaseAuthToken_${currentUserUID}`;
      const token = sessionStorage.getItem(tokenKey);
      
      if (token) {
        console.log('Configuration des en-têtes d\'autorisation avec le token');
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      } else {
        console.log('Aucun token trouvé, impossible de configurer les en-têtes');
        // Si pas de token, supprimer l'en-tête d'autorisation
        delete api.defaults.headers.common['Authorization'];
      }
    } catch (error) {
      console.error('Erreur lors de la configuration des en-têtes d\'autorisation:', error);
    }
  }

  /**
   * Stocke le token dans le sessionStorage
   */
  private storeToken(token: string, uid: string): void {
    if (!uid) {
      console.error('Tentative de stockage d\'un token sans UID valide');
      return;
    }
    
    console.log(`🔑 Stockage du token pour l'UID: ${uid.substring(0, 6)}...`);
    
    const tokenKey = `firebaseAuthToken_${uid}`;
    try {
      // Stocker le token pour cet utilisateur
      sessionStorage.setItem(tokenKey, token);
      
      // Stocker l'UID courant
      sessionStorage.setItem('currentUserUID', uid);
      
      // Marquer l'utilisateur comme connecté
      localStorage.setItem('isLoggedIn', 'true');
      
      // Stocker les données d'authentification actualisées dans une variable locale pour débogage
      const currentUID = sessionStorage.getItem('currentUserUID');
      console.log(`🔑 Vérification de l'UID stocké: ${currentUID ? currentUID.substring(0, 6) + '...' : 'null'}`);
      
      // Configure immédiatement les en-têtes d'autorisation
      this.configureAuthHeaders();
    } catch (error) {
      console.error('Erreur lors du stockage du token:', error);
    }
  }

  /**
   * Nettoie les données d'authentification
   */
  private clearAuthData(): void {
    // Supprime tous les tokens de session
    const currentUserUID = sessionStorage.getItem('currentUserUID');
    if (currentUserUID) {
      sessionStorage.removeItem(`firebaseAuthToken_${currentUserUID}`);
    }
    
    sessionStorage.removeItem('currentUserUID');
    sessionStorage.removeItem('userData');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('auth_state');
  }

  /**
   * Traduit les codes d'erreur Firebase en messages conviviaux
   */
  private getFirebaseErrorMessage(errorCode?: string): string {
    if (!errorCode) return 'Une erreur inconnue s\'est produite';
    
    switch (errorCode) {
      case 'auth/invalid-email':
        return 'L\'adresse e-mail n\'est pas valide';
      case 'auth/user-disabled':
        return 'Ce compte a été désactivé';
      case 'auth/user-not-found':
        return 'Aucun compte ne correspond à cette adresse e-mail';
      case 'auth/wrong-password':
        return 'Le mot de passe est incorrect';
      case 'auth/email-already-in-use':
        return 'Cette adresse e-mail est déjà utilisée';
      case 'auth/weak-password':
        return 'Le mot de passe est trop faible';
      case 'auth/operation-not-allowed':
        return 'Cette opération n\'est pas autorisée';
      case 'auth/account-exists-with-different-credential':
        return 'Un compte existe déjà avec la même adresse e-mail mais des informations d\'identification différentes';
      case 'auth/popup-closed-by-user':
        return 'La fenêtre de connexion a été fermée avant la fin du processus';
      case 'auth/popup-blocked':
        return 'La fenêtre de connexion a été bloquée par le navigateur';
      case 'auth/cancelled-popup-request':
        return 'La demande de connexion a été annulée';
      case 'auth/unauthorized-domain':
        return 'Ce domaine n\'est pas autorisé à utiliser l\'authentification';
      case 'auth/invalid-action-code':
        return 'Le code d\'action n\'est pas valide';
      default:
        return 'Une erreur s\'est produite lors de l\'authentification';
    }
  }
}

// Exporte une instance du service
export const authService = AuthService.getInstance();
